import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, Plus, Trash2, Loader2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface OnboardingStep {
  title: string;
  description?: string;
  durationDays?: number;
}

interface OnboardingTemplate {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  steps: OnboardingStep[];
}

export default function OnboardingTemplatesPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<{ name: string; description: string; steps: OnboardingStep[]; isActive: boolean }>({
    name: "", description: "", steps: [], isActive: true,
  });

  const { data: templates = [] } = useQuery<OnboardingTemplate[]>({
    queryKey: ["/api/onboarding-templates"],
    queryFn: () => api.get<OnboardingTemplate[]>(`/onboarding-templates`, sessionId),
  });

  const saveMutation = useMutation({
    mutationFn: () => editingId
      ? api.patch(`/onboarding-templates/${editingId}`, form, sessionId)
      : api.post(`/onboarding-templates`, form, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/onboarding-templates"] });
      setDialogOpen(false);
      setEditingId(null);
      toast({ title: editingId ? "Template updated" : "Template created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/onboarding-templates/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/onboarding-templates"] });
      toast({ title: "Template deleted" });
    },
  });

  const handleNew = () => {
    setEditingId(null);
    setForm({ name: "", description: "", steps: [{ title: "" }], isActive: true });
    setDialogOpen(true);
  };

  const handleEdit = (t: OnboardingTemplate) => {
    setEditingId(t.id);
    setForm({
      name: t.name,
      description: t.description || "",
      steps: t.steps.length > 0 ? t.steps : [{ title: "" }],
      isActive: t.isActive,
    });
    setDialogOpen(true);
  };

  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, { title: "" }] }));
  const removeStep = (idx: number) => setForm((f) => ({ ...f, steps: f.steps.filter((_, i) => i !== idx) }));
  const updateStep = (idx: number, patch: Partial<OnboardingStep>) =>
    setForm((f) => ({ ...f, steps: f.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)) }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Onboarding Templates"
        description="Create and manage onboarding templates for new customers"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={handleNew} data-testid="button-new-template">
              <Plus className="h-4 w-4 mr-1" /> New Template
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardList className="h-4 w-4" /> Templates ({templates.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No templates yet. Onboarding templates help streamline the customer setup process.
            </p>
          ) : (
            <div className="space-y-2">
              {templates.map((t) => (
                <div key={t.id} className="flex items-start justify-between py-3 px-3 rounded-md border">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{t.name}</span>
                      <Badge variant="outline" className="text-[10px]">{t.steps.length} step{t.steps.length === 1 ? "" : "s"}</Badge>
                      {!t.isActive && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                    </div>
                    {t.description && <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>}
                    {t.steps.length > 0 && (
                      <ol className="mt-2 text-xs text-muted-foreground list-decimal list-inside space-y-0.5">
                        {t.steps.map((s, i) => (
                          <li key={i}>
                            {s.title}
                            {s.durationDays ? ` · ${s.durationDays}d` : ""}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(t)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => { if (confirm(`Delete template "${t.name}"?`)) deleteMutation.mutate(t.id); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Template" : "New Onboarding Template"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Name *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Standard Foodservice Onboarding"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="When to use this template..."
                rows={2}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium">Steps</label>
                <Button type="button" size="sm" variant="outline" onClick={addStep}>
                  <Plus className="h-3 w-3 mr-1" /> Add Step
                </Button>
              </div>
              <div className="space-y-2">
                {form.steps.map((step, idx) => (
                  <div key={idx} className="flex items-end gap-2 p-2 border rounded-md">
                    <div className="flex-1">
                      <label className="text-xs">Step {idx + 1} title</label>
                      <Input
                        value={step.title}
                        onChange={(e) => updateStep(idx, { title: e.target.value })}
                        placeholder="e.g., Send welcome packet"
                      />
                    </div>
                    <div className="w-24">
                      <label className="text-xs">Days</label>
                      <Input
                        type="number"
                        value={step.durationDays ?? ""}
                        onChange={(e) => updateStep(idx, { durationDays: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                      />
                    </div>
                    <Button
                      type="button" variant="ghost" size="icon" className="h-9 w-9"
                      onClick={() => removeStep(idx)} disabled={form.steps.length <= 1}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Active
            </label>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={!form.name || saveMutation.isPending}
              >
                {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? "Save Changes" : "Create Template"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
